import {
  AlertCircle,
  CheckCircle2,
  FileText,
  ImageIcon,
  LoaderCircle,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import { useState } from "react";

import { IncidentAttachmentsPicker } from "@/modules/dashboard/payment-incidents/components/IncidentAttachmentsPicker";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import {
  WALLET_DEBIT_ATTACHMENTS_MAX,
  WALLET_DEBIT_ATTACHMENTS_PER_UPLOAD,
} from "../constants/walletDebits.constants";
import { useWalletDebitAttachments, type WalletDebitUploadState } from "../hooks/useWalletDebitAttachments";
import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebit, WalletDebitAttachment } from "../types/walletDebits.types";
import { formatFileSize } from "../utils/walletDebit";

export interface WalletDebitAttachmentsProps {
  debit: WalletDebit;
  /** Allowlisted staff may add and remove proof; everyone else only reads it. */
  canEdit: boolean;
  onChange: (debit: WalletDebit) => void;
}

/**
 * The proof of a manual send: what is attached, an upload in flight or failed, a way to add more and to take
 * one off (it stays stored, and the timeline names it).
 */
export function WalletDebitAttachments({ debit, canEdit, onChange }: WalletDebitAttachmentsProps) {
  const { t } = useTranslations("walletDebits");
  const [files, setFiles] = useState<File[]>([]);
  const own = useWalletDebitAttachments(onChange);
  const room = Math.max(0, WALLET_DEBIT_ATTACHMENTS_MAX - debit.attachments.length);
  const uploading = own.state.status === "uploading";

  const send = async () => {
    if (await own.upload(debit.id, files)) setFiles([]);
  };

  return (
    <section aria-labelledby={`debit-attachments-${debit.id}`} className="space-y-3">
      <h3 id={`debit-attachments-${debit.id}`} className="text-sm font-semibold text-foreground">
        {t("attachments_title")}
      </h3>

      <UploadStatus state={own.state} onRetry={() => void send()} />

      {debit.attachments.length > 0 ? (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {debit.attachments.map((file) => (
            <li key={file.id}>
              <AttachmentRow debitId={debit.id} file={file} canRemove={canEdit} onChange={onChange} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{t("attachments_empty")}</p>
      )}

      {canEdit &&
        (room > 0 ? (
          <div className="space-y-2">
            <IncidentAttachmentsPicker
              value={files}
              onChange={setFiles}
              disabled={uploading}
              max={Math.min(WALLET_DEBIT_ATTACHMENTS_PER_UPLOAD, room)}
            />
            {files.length > 0 && (
              <ColocaButton
                size="sm"
                variant="primary"
                icon={Upload}
                loading={uploading}
                disabled={uploading}
                onClick={() => void send()}
              >
                {t("attachments_upload")}
              </ColocaButton>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t("attachments_full", { max: WALLET_DEBIT_ATTACHMENTS_MAX })}
          </p>
        ))}
    </section>
  );
}

function UploadStatus({ state, onRetry }: { state: WalletDebitUploadState; onRetry: () => void }) {
  const { t } = useTranslations("walletDebits");
  if (state.status === "uploading") {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
        {t("attachments_uploading")}
      </p>
    );
  }
  if (state.status === "done") {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-success">
        <CheckCircle2 className="size-4" aria-hidden="true" />
        {t("attachments_uploaded")}
      </p>
    );
  }
  if (state.status === "failed") {
    return (
      <ColocaAlert
        variant="destructive"
        icon={AlertCircle}
        text={t("attachments_failed", { message: state.error })}
        actions={
          <ColocaButton size="sm" variant="secondary" icon={RefreshCw} onClick={onRetry}>
            {t("attachments_retry")}
          </ColocaButton>
        }
      />
    );
  }
  return null;
}

function AttachmentRow({
  debitId,
  file,
  canRemove,
  onChange,
}: {
  debitId: string;
  file: WalletDebitAttachment;
  canRemove: boolean;
  onChange: (debit: WalletDebit) => void;
}) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const Icon = file.content_type.startsWith("image/") ? ImageIcon : FileText;

  const remove = async () => {
    setRemoving(true);
    setError(null);
    try {
      onChange(await WalletDebitsService.removeAttachment(debitId, file.id));
    } catch (removeError: unknown) {
      setError(HttpApiService.getErrorMessage(removeError));
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-2 px-3 py-2.5">
      <div className="flex items-center gap-3">
        <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          {file.url ? (
            <a
              href={file.url}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={t("attachments_open", { name: file.filename })}
              className="block truncate text-sm font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
            >
              {file.filename}
            </a>
          ) : (
            <span className="block truncate text-sm font-medium text-foreground">
              {file.filename}
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {t("attachments_unavailable")}
              </span>
            </span>
          )}
          <span className="block truncate text-xs text-muted-foreground">
            {t("attachments_meta", {
              size: formatFileSize(file.size, activeLanguage),
              email: file.uploaded_by_email,
            })}
          </span>
        </div>
        {canRemove && !confirming && (
          <ColocaButton
            size="sm"
            variant="ghost"
            icon={Trash2}
            aria-label={t("attachments_remove_aria", { name: file.filename })}
            onClick={() => setConfirming(true)}
          >
            {t("attachments_remove")}
          </ColocaButton>
        )}
      </div>
      {confirming && (
        <div className="flex flex-wrap items-center gap-2 pl-7">
          <span className="text-xs text-muted-foreground">{t("attachments_remove_confirm")}</span>
          <ColocaButton
            size="sm"
            variant="destructive"
            loading={removing}
            disabled={removing}
            onClick={() => void remove()}
          >
            {t("attachments_remove_yes")}
          </ColocaButton>
          <ColocaButton size="sm" variant="ghost" disabled={removing} onClick={() => setConfirming(false)}>
            {t("action_cancel")}
          </ColocaButton>
        </div>
      )}
      {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
    </div>
  );
}
